const CART_SELECTOR = '[data-goalstyle-market-cart]';
const CONFIG_SELECTOR = '[data-goalstyle-market-config]';
const PRODUCT_PRICE_SELECTOR = '[data-goalstyle-market-product-price]';

function forEachMatch(root, selector, callback) {
  if (root instanceof Element && root.matches(selector)) callback(root);
  root.querySelectorAll?.(selector).forEach(callback);
}

function getMarketConfig() {
  const el = document.querySelector(CONFIG_SELECTOR);
  if (!el) return {};

  try {
    return JSON.parse(el.textContent);
  } catch {
    return {};
  }
}

function applyHeaderMarketLabel(root = document) {
  const currencyLabels = [];
  forEachMatch(root, '[data-testid="localization-currency-code"]', (el) => currencyLabels.push(el));
  if (!currencyLabels.length) return;

  const { country } = getMarketConfig();
  const labels = {
    CA: 'CAD',
    CO: 'COP',
    US: 'USD',
    MX: 'MXN',
  };
  const label = labels[country] || 'USD';

  currencyLabels.forEach((el) => {
    if (el.textContent.trim() !== label) el.textContent = label;
  });
}

function formatGroupedInteger(value, separator) {
  return `${Math.round(Number(value) || 0)}`.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function formatLocalAmountOnly(amount, currency) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '';

  if (currency === 'CAD') {
    return Number.isInteger(numeric) ? `${numeric}` : numeric.toFixed(2);
  }

  if (currency === 'COP') return formatGroupedInteger(numeric, '.');
  if (currency === 'MXN') return formatGroupedInteger(numeric, ',');

  return `${numeric}`;
}

function marketCurrencyPrefix(currency) {
  const prefixes = {
    CAD: 'CA$',
    COP: 'COP $',
    MXN: 'MXN $',
  };
  return prefixes[currency] || '';
}

function getCurrentVariantId(wrapper, variants) {
  const available = variants || {};
  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant && available[urlVariant]) return urlVariant;

  const scope =
    wrapper.closest('product-information') ||
    wrapper.closest('[data-product-id]') ||
    document;

  const picker = scope.querySelector?.('variant-picker') || document.querySelector('variant-picker');
  const selectedVariantScript = picker?.querySelector('script[type="application/json"]');
  if (selectedVariantScript) {
    try {
      const selectedVariant = JSON.parse(selectedVariantScript.textContent);
      const id = selectedVariant?.id != null ? `${selectedVariant.id}` : '';
      if (id && available[id]) return id;
    } catch {
      // Keep checking other variant signals.
    }
  }

  const checked = [...scope.querySelectorAll?.('[data-variant-id]:checked') || []].reverse();
  for (const input of checked) {
    const id = `${input.dataset.variantId || ''}`;
    if (id && available[id]) return id;
  }

  const selected = [...scope.querySelectorAll?.('[data-variant-id][aria-checked="true"]') || []].reverse();
  for (const input of selected) {
    const id = `${input.dataset.variantId || ''}`;
    if (id && available[id]) return id;
  }

  return '';
}

function applyProductMarketPrice(wrapper) {
  const dataEl = wrapper.querySelector('[data-goalstyle-product-market-prices]');
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const variants = data.variants || {};
  const variantId = getCurrentVariantId(wrapper, variants) || `${data.initialVariantId || ''}`;
  const prices = variants[variantId];
  if (!prices || prices.local == null) return;

  const prefix = marketCurrencyPrefix(data.currency);
  const amountOnly = formatLocalAmountOnly(prices.local, data.currency);
  const checkoutValue = `US$${(Number(prices.usdCents || 0) / 100).toFixed(2)}`;

  const prefixEl = wrapper.querySelector('[data-goalstyle-market-currency-prefix]');
  if (prefixEl && prefixEl.textContent !== prefix) prefixEl.textContent = prefix;

  const amountEl = wrapper.querySelector('[data-goalstyle-market-price-number]');
  if (amountEl && amountEl.textContent.trim() !== amountOnly) amountEl.textContent = amountOnly;

  const checkoutEl = wrapper.querySelector('[data-goalstyle-market-checkout-value]');
  if (checkoutEl && checkoutEl.textContent.trim() !== checkoutValue) checkoutEl.textContent = checkoutValue;

  const checkoutRow = wrapper.querySelector('.gs-market-checkout-row');
  if (checkoutRow && data.checkoutLabel) {
    const ariaLabel = `${data.checkoutLabel} ${checkoutValue}`;
    if (checkoutRow.getAttribute('aria-label') !== ariaLabel) checkoutRow.setAttribute('aria-label', ariaLabel);
  }

  wrapper.dataset.goalstyleCurrentVariantId = variantId;
}

function applyProductMarketPrices(root = document) {
  forEachMatch(root, PRODUCT_PRICE_SELECTOR, applyProductMarketPrice);
}

function cleanCustomizationOptions(root = document) {
  forEachMatch(root, '.cart-items__variants', (list) => {
    const variants = [...list.querySelectorAll('.cart-items__variant')];

    variants.forEach((variant) => {
      const label = variant.querySelector('dt')?.textContent?.trim().toLowerCase() || '';
      if (label.startsWith('customization')) variant.remove();
    });

    const remaining = [...list.querySelectorAll('.cart-items__variant')];
    remaining.forEach((variant, index) => {
      const value = variant.querySelector('dd');
      if (!value) return;
      const clean = value.textContent.replace(/,\s*$/, '').trim();
      const next = index < remaining.length - 1 ? `${clean},\u00a0` : clean;
      if (value.textContent !== next) value.textContent = next;
    });
  });
}

function insertCheckoutNote(scope, note) {
  if (!scope || !note) return;

  let noteEl = scope.querySelector('.gs-market-checkout-note');
  if (!noteEl) {
    noteEl = document.createElement('p');
    noteEl.className = 'gs-market-checkout-note';
  }

  if (noteEl.textContent !== note) noteEl.textContent = note;

  const ctas = scope.querySelector('.cart__ctas');
  if (ctas && noteEl.nextElementSibling !== ctas) {
    ctas.before(noteEl);
  } else if (!noteEl.isConnected) {
    scope.append(noteEl);
  }
}

function applyMarketCartData(dataEl) {
  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const scope =
    dataEl.closest('.cart-drawer__summary') ||
    dataEl.closest('.cart-summary__inner') ||
    dataEl.parentElement;

  const cartRoot = dataEl.closest('cart-items-component') || document;

  Object.entries(data.items || {}).forEach(([key, prices]) => {
    cartRoot.querySelectorAll('.cart-items__table-row').forEach((row) => {
      if (row.dataset.key !== key) return;

      const linePrice = row.querySelector('.cart-items__price text-component');
      if (linePrice && linePrice.textContent.trim() !== prices.line) {
        linePrice.textContent = prices.line;
        linePrice.setAttribute('value', prices.line);
      }

      const unitPrice = row.querySelector('.cart-items__unit-price-wrapper > div');
      if (unitPrice && unitPrice.textContent.trim() !== prices.unit) {
        unitPrice.textContent = prices.unit;
      }
    });
  });

  if (scope) {
    const total = scope.querySelector('.cart-totals__total-value');
    if (total && total.textContent.trim() !== data.total) {
      total.textContent = data.total;
      total.setAttribute('value', data.total);
    }

    const subtotal = scope.querySelector('.cart-totals__original-value');
    if (subtotal && subtotal.textContent.trim() !== data.subtotal) {
      subtotal.textContent = data.subtotal;
    }

    insertCheckoutNote(scope, data.note);
  }
}

const pendingRoots = new Set();
let scheduled = false;

function relevantRoot(node) {
  const element = node instanceof Element ? node : node?.parentElement;
  if (!element) return null;

  if (element.closest(CONFIG_SELECTOR)) return document;
  if (element.closest('localization-form, [name="country_code"]')) return document;

  return (
    element.closest(PRODUCT_PRICE_SELECTOR) ||
    element.closest('product-information') ||
    element.closest('[data-product-id]') ||
    element.closest('cart-items-component') ||
    element.closest('[data-testid="localization-currency-code"]') ||
    null
  );
}

function scheduleMutationNode(node) {
  const element = node instanceof Element ? node : node?.parentElement;
  if (!element) return;

  const context = relevantRoot(element);
  if (context) {
    scheduleApply(context);
    return;
  }

  if (!(node instanceof Element)) return;

  const selectors = [
    PRODUCT_PRICE_SELECTOR,
    'cart-items-component',
    '.cart-items__variants',
    CART_SELECTOR,
    '[data-testid="localization-currency-code"]',
    CONFIG_SELECTOR,
  ];

  selectors.forEach((selector) => {
    forEachMatch(node, selector, (match) => {
      scheduleApply(relevantRoot(match) || match);
    });
  });
}

function collapseRoots(roots) {
  return roots.filter((root) => !roots.some(
    (other) => other !== root && (other === document || other.contains?.(root))
  ));
}

function applyAll(root = document) {
  applyHeaderMarketLabel(root);
  applyProductMarketPrices(root);
  cleanCustomizationOptions(root);
  forEachMatch(root, CART_SELECTOR, applyMarketCartData);
}

function scheduleApply(root = document) {
  if (root instanceof Element) pendingRoots.add(root);
  else pendingRoots.add(document);
  if (scheduled) return;

  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    const roots = collapseRoots([...pendingRoots]);
    pendingRoots.clear();
    roots.forEach(applyAll);
  });
}

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach(scheduleMutationNode);

    if (mutation.removedNodes.length) {
      const root = relevantRoot(mutation.target);
      if (root) scheduleApply(root);
    }
  });
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
});

function scheduleRelevantTarget(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('localization-form, [name="country_code"]')) {
    scheduleApply(document);
    return;
  }

  const root = relevantRoot(target);
  if (root) scheduleApply(root);
}

document.addEventListener('change', scheduleRelevantTarget);
document.addEventListener('input', scheduleRelevantTarget);
document.addEventListener('click', (event) => {
  if (!event.target.closest?.('[data-variant-id], variant-picker')) return;
  const root = relevantRoot(event.target);
  if (root) scheduleApply(root);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleApply(document);
});
window.addEventListener('popstate', () => scheduleApply(document));
scheduleApply();
